import Vue from "vue";
import Vuex from "vuex";

Vue.use(Vuex);

/**
 * Stores the Informations for the Input Quantity Vue Component
 */
const store = new Vuex.Store({
    state: {
        updateUrl: null,
        delayTime: null,
        productMap: null,
        isOrderReviewPage: null,
        isCheckoutPage: null,
        isPaymentPage: null,
        productConversionList: {},
        basketInfo: {},
        couponsInfo: {},
        orderSummaryInfo: {},
        productMeasure: {},
        shipmentDeliverySlots: {},
        shipmentInformation: [],
    },
    mutations: {
        /**
         * Sets the Update URL
         * @param {object} state - vuex store state
         * @param {string} url - the Update URL
         */
        setUpdateUrl(state, url) {
            state.updateUrl = url;
        },
        /**
         * Sets the Dimension Update URL
         * @param {object} state - vuex store state
         * @param {string} url - the Dimension Update URL
         */
        setDimensionUpdateUrl(state, url) {
            state.dimensionUpdateUrl = url;
        },
        /**
         * Sets the "Add To Cart" Delay Time
         * @param {object} state - vuex store state
         * @param {string} newDelay - the delay time
         */
        setDelayTime(state, newDelay) {
            state.delayTime = newDelay || 0;
        },
        /**
         * Sets the Product Map
         * @param {object} state - vuex store state
         * @param {object} obj - the latest product map
         */
        setProductMap(state, obj) {
            state.productMap = obj.items;
        },
        /**
         * Adds/Updates a Product Conversion Information
         * @param {object} state - vuex store state
         * @param {object} payload - a product information
         */
        addProductConversion(state, payload) {
            state.productConversionList[payload.pid] = payload.productConversion;
        },
        /**
         * Sets the Basket Information
         * @param {object} state - vuex store state
         * @param {object} obj - the latest basket info
         */
        setBasketInfo(state, obj) {
            state.basketInfo = obj;
        },
        /**
         * Sets the Coupons Information
         * @param {object} state - vuex store state
         * @param {object} obj - the latest coupons info
         */
        setCouponsInfo(state, obj) {
            state.couponsInfo = obj;
        },
        /**
         * Sets the Order Summary Information
         * @param {object} state - vuex store state
         * @param {object} obj - the latest order summary info
         */
        setOrderSummaryInfo(state, obj) {
            state.orderSummaryInfo = obj;
        },
        /**
         * Sets updated Product Measure Information
         * @param {object} state - vuex store state
         * @param {object} obj - the latest measure information
         */
        setProductMeasure(state, payload) {
            state.productMeasure[payload.pid] = payload.measurementInfo;
        },
        /**
         * Sets the Delivery Slots Information
         * @param {object} state - vuex store state
         * @param {object} obj - the latest Delivery Slots info
         */
        setShipmentDeliverySlots(state, obj) {
            state.shipmentDeliverySlots = obj;
        },
        /**
         * Sets the Shipment Information
         * @param {object} state - vuex store state
         * @param {object} payload - the shipment information
         */
        setShipmentInformation(state, payload) {
            state.shipmentInformation = payload;
        },
        /**
         * Sets the boolean for Order Review being the current page
         * @param {object} state - vuex store state
         * @param {object} isOrderReviewPage - boolean based on current page being order review
         */
        setOrderReviewPageBoolean(state, isOrderReviewPage) {
            state.isOrderReviewPage = isOrderReviewPage;
        },
        /**
         * Sets the boolean for Checkout being the current page
         * @param {object} state - vuex store state
         * @param {object} isCheckoutPage - boolean based on current page being checkout
         */
        setCheckoutPageBoolean(state, isCheckoutPage) {
            state.isCheckoutPage = isCheckoutPage;
        },
        /**
         * Sets the boolean for Payment being the current page
         * @param {object} state - vuex store state
         * @param {object} isPaymentPage - boolean based on current page being checkout
         */
        setPaymentPageBoolean(state, isPaymentPage) {
            state.isPaymentPage = isPaymentPage;
        },
        /**
         * Sets the Shipment Information for the current shipment
         * @param {object} state - vuex store state
         * @param {object} obj - the shipment information
         */
        setShipmentData(state, obj) {
            const shipmentData = this.getters.getShipmentData(obj.shipmentID);
            if (shipmentData) {
                const shipmentsInfo = state.shipmentInformation;
                shipmentsInfo.forEach((shipment, index, array) => {
                    if (shipment.shipmentID == obj.shipmentID) {
                        array.splice(index, 1, obj);
                    }
                });
            } else {
                state.shipmentInformation.push(obj);
            }

        }
    },
    getters: {
        /**
         * Returns a Product Conversion Information
         * @param {object} state - vuex store state
         * @param {string} prodID - the product id
         * @return {object} - the conversion information
         */
        getProductConversion: state => prodID => state.productConversionList[prodID],
        /**
         * Returns a Product's Information from the Products Map
         * @param {object} state - vuex store state
         * @param {string} prodID - the product id
         * @param {string} info - the information requested
         * @return {string} - the product's information
         */
        getProductInfo: state => (prodID, info) => {
            if (state.productMap) {
                const productFound = state.productMap.find(prod => prod.id === prodID);

                if (productFound) {
                    return productFound[info];
                }
            }

            return null;
        },
        /**
         * Returns updated Product Measure Information
         * @param {object} state - vuex store state
         * @param {string} pid - the product id
         * @return {object} - the measure information
         */
        getProductMeasure: state => pid => {
            if (state.productMeasure) {
                return state.productMeasure[pid];
            }

            return null;
        },
        /**
         * Returns the Shipment Information
         * @param {object} state - vuex store state
         */
        getShipmentInformation: state => () => state.shipmentInformation,
        /**
         * Returns the Shipment Delivery Slots
         * @param {object} state - vuex store state
         */
        getShipmentDeliverySlots: state => () => state.shipmentDeliverySlots,
        /**
         * Returns the Order Summary Shipment Information
         * @param {object} state - vuex store state
         */
        getOrderSummaryInfo: state => () => state.orderSummaryInfo,
        /**
         * Returns boolean if the current page is Order Review
         * @param {object} state - vuex store state
         */
        getOrderReviewPageBoolean: state => () => state.isOrderReviewPage,
        /**
         * Returns boolean if the current page is Checkout Page
         * @param {object} state - vuex store state
         */
        getCheckoutPageBoolean: state => () => state.isCheckoutPage,
        /**
         * Returns boolean if the current page is Payment Page
         * @param {object} state - vuex store state
         */
        getPaymentPageBoolean: state => () => state.isPaymentPage,
        /**
         * Returns updated shipment Information
         * @param {object} state - vuex store state
         * @param {string} shipmentID - the shipment id
         * @return {object} - the measure information
         */
        getShipmentData: state => shipmentID =>  {
            return state.shipmentInformation.find(shipmentData => {
                return shipmentData.shipmentID == shipmentID;
            });
        }
    }
});

export default store;
